import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import moment from "moment";
import produce from "immer";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Card, CardContent, Grid, Tab, Tabs, Typography } from "@material-ui/core";
import { TabContext, TabPanel } from "@material-ui/lab";

import CustomAlert from "../../../components/common/CustomAlert/CustomAlert";
import Loader from "../../../components/common/Loader/Loader";
import ConfirmModal from "../../common/ConfirmModal/ConfirmModal";
import LinkButton from "../../common/LinkButton/LinkButton";
import PageTitle from "../../common/PageTitle/PageTitle";
import PrimaryButton from "../../common/PrimaryButton/PrimaryButton";
import RemoveButton from "../../common/RemoveButton/RemoveButton";
import AreaTransferList from "../AreaTransferList/AreaTransferList";
import BasicServiceDetails from "../common/BasicServiceDetails/BasicServiceDetails";
import defaultServiceStyles from "./DefaultServiceStyles";
import EditSlotRange from "./RangeGeneratorForm/EditSlotRange";
import RangeGeneratorForm from "./RangeGeneratorForm/RangeGeneratorForm";

import AdditionalZoneConfiguration from "./AdditionalZoneConfiguration/AdditionalZoneConfiguration";

import { ALERT_TYPES, APP_ROUTES, APPLICATION_TEXT_CONSTANT, BUTTON_TEXT_CONSTANT, DELIVERY_TYPE, SERVICE_TYPES } from "../../../config/constants";
import { serviceSelectPropositionRecord } from "../../../config/redux/configurationsSlice";
import { fetchPOSDetailsByPOSNo } from "../../CreatePOS/redux/posSlice";

import { deleteZoneConfig, deleteDefaultTemplateSlotBySlotId, fetchSlotsInformationByServiceType, makeDefaultTemplateSlotEditable, resetSlotsInformation } from "../redux/templateSlotsSlice";
import { ALL_DAYS_VALUES } from "../utils/constants";
import { calculateAvailableDaysForSelection, createNextZoneName } from "../utils/serviceUtils";
import {
  add_new_template_slot_util,
  createPayloadForSavedefaultConfig,
  createPayloadForUpdatedefaultConfig,
  executeScroll,
  getMissingDeliveryTypeFromObject,
  isAddOtherDaysButtonDisabled,
  resolveTabForSave,
  saveDefaultZoneConfigurations,
  updateTemplateSlotHandler,
  updateZoneConfigurations,
  validateSaveDefaultConfigObject,
  validateUpdateSlotConfigObject,
} from "./base/DefaultUtils";
import { ConfigTabsState, currentAllowedDeliveryTypes, DefaultServiceProps, DEFAULT_CONFIG_TAB, DEFAULT_RANGE_GENERATOR_FORM_VALUES, slotsGeneratorFormsnterface, tabInitValues, TEXT_CONSTANT } from "./DefaultInterface";
import { isArrayValid, isEmptyOrNull } from "../../../utils/DataUtils";
import { generateElementIdentifier } from "utils/ApplicationUtils";
import { DATA_IDENTIFIER, ELEMENT } from "components/common/App/app-constant";

const DefaultService = (props: DefaultServiceProps) => {
  const { serviceTypeId, serviceTypeName, isPOSMappingAreaBased, displayName } = props;
  const { posNo } = useParams<any>();
  const reg = new RegExp("^[0-9]+$"); //NOSONAR
  const classes = defaultServiceStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rangeGeneratorFormRef = React.useRef(null);
  const { posDetails, loading: fetchingDetails, fetching: fetchingPosDetails } = useSelector((state: any) => state.pos);
  const { fetchingSlots, default: defaultService } = useSelector((state: any) => ({ ...state.slots, default: state.slots[serviceTypeName] || [] }));
  const servicePropositionList = useSelector(serviceSelectPropositionRecord);
  const [selectedZoneTab, setSelectedZoneTab] = useState(0);
  const [showDeleteZCModal, setShowDeleteZCModal] = useState(false);
  const [configTabs, setConfigTabs] = useState<Array<ConfigTabsState>>(tabInitValues);

  const resetConfigTab = (tabIndexToBeReset: number) => {
    let defaultConfig: any = { ...DEFAULT_CONFIG_TAB };
    let newTabs = produce(configTabs, (draft) => {
      draft[tabIndexToBeReset] = { ...defaultConfig, zoneName: `Zone01` };
    });
    setConfigTabs(newTabs);
  };

  const removeZoneTabAfterDeletion = (tabIndexToBeDeleted: number) => {
    if (configTabs.length > 1) {
      let newTabs = produce(configTabs, (draft) => {
        draft.splice(tabIndexToBeDeleted, 1);
      });
      setSelectedZoneTab(configTabs.length - 2);
      setConfigTabs(newTabs);
    } else {
      resetConfigTab(tabIndexToBeDeleted);
    }
  };

  /**
   * Method to delete the zone config
   */
  const deleteConfigTab = async (tabIndexToBeDeleted: number) => {
    let zoneId: any = configTabs[tabIndexToBeDeleted].zoneId;
    // If the tabs config is being saved at the backend, call the API to delete
    if (zoneId) {
      const {
        payload: { data, message: deleteError },
      } = await dispatch(deleteZoneConfig({ serviceTypeName, zoneConfigId: configTabs[tabIndexToBeDeleted].zoneId, posNo: posNo }));
      setShowDeleteZCModal(false);
      if (data === true || data?.success) {
        CustomAlert(ALERT_TYPES.SUCCESS, "Zone configuration deleted successfully!");
        removeZoneTabAfterDeletion(tabIndexToBeDeleted);
      } else if (deleteError) {
        CustomAlert(ALERT_TYPES.ERROR, deleteError);
        return;
      }
    } else if (!zoneId) {
      removeZoneTabAfterDeletion(tabIndexToBeDeleted); // If the tab data is not yet saved at the backend, no need to call backend API
      setShowDeleteZCModal(false);
    }
  };

  const handleAddZoneTabClick = () => {
    let newConfigTab = { ...DEFAULT_CONFIG_TAB, zoneName: createNextZoneName(configTabs) };
    let newTabs: any = [...configTabs, newConfigTab];
    setConfigTabs(newTabs);
    setSelectedZoneTab(configTabs.length);
  };

  const handleAddOtherDaysClick = (tabIndex: number, e: any, objectKey: any) => {
    e.stopPropagation();
    let newForm: any = { ...DEFAULT_RANGE_GENERATOR_FORM_VALUES };
    let newTabs = produce(configTabs, (draft) => {
      draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface].push(newForm);
    });
    setConfigTabs(newTabs);
    setTimeout(() => {
      executeScroll(rangeGeneratorFormRef);
    }, 100);
  };

  const handleDaysChange = (tabIndex: number, generatorFormIndex: number, value: any, objectKey: any) => {
    let newTabs = produce(configTabs, (draft) => {
      draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][generatorFormIndex].days = value;
    });
    setConfigTabs(newTabs);
  };

  const onIndividualCutOffFieldsChange = (tabIndex: number, formIndex: number, rangeIndex: number, objectKey: any, date: string) => {
    let newTabs: any = produce(configTabs, (draft: any) => {
      draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][formIndex].generatedRange[rangeIndex].cutOff = date;
    });
    setConfigTabs(newTabs);
  };

  const handleGenerateRangeSubmit = (tabIndex: number, formIndex: number, objectKey: any, formDataList?: Array<{ minimumDays: any; maximumDays: any; cutOff: any; priorityFee: any }>) => {
    const forms = formDataList ?? [configTabs[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][formIndex]];
    let ranges: Array<any> = [];
    for (const form of forms) {
      if (parseInt(form.minimumDays) > parseInt(form.maximumDays)) {
        CustomAlert(ALERT_TYPES.ERROR, "Minimum days cannot be greater than maximum days");
        return;
      }
      ranges.push({ dateRange: `${form.minimumDays} - ${form.maximumDays}`, cutOff: form.cutOff, priorityFee: form.priorityFee, minimumDays: form.minimumDays, maximumDays: form.maximumDays });
    }
    let newTabs = produce(configTabs, (draft) => {
      draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][formIndex].generatedRange = ranges;
    });
    setConfigTabs(newTabs);
  };

  const handleAssignedAreas = (configTabIndex: number, assignedAreas: any) => {
    let newTabs = produce(configTabs, (draft) => {
      draft[configTabIndex].selectedAreas = assignedAreas.map((area: any) => area.id);
    });
    setConfigTabs(newTabs);
  };

  const renderAssignedAreas = (configTab: any, configTabIndex: number) => {
    return <AreaTransferList selectedAreas={configTab.selectedAreas} handleAssignedAreaListChange={(assignedAreas: any) => handleAssignedAreas(configTabIndex, assignedAreas)} />;
  };

  const cancelEditingASlot = (tabIndex: number, generatorFormIndex: number, index: number, slot: any, objectKey: any) => {
    if (!slot) return;

    // New slot that was never saved — remove it from configTabs
    if (!slot?.id || slot?.isNewSlot) {
      let newTabs = produce(configTabs, (draft: any) => {
        draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][slot.day].splice(index, 1);
      });
      setConfigTabs(newTabs);
      return;
    }

    dispatch(makeDefaultTemplateSlotEditable({ serviceTypeName, slotData: slot, canBeUpdated: false, objectKey }));

    let unEditedSlotFromStore = defaultService?.[tabIndex]?.supplierTypes?.[objectKey]?.dayWiseSlots?.[slot?.day]?.find((item: any) => item.id === slot.id);
    let newTabs = produce(configTabs, (draft: any) => {
      draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][unEditedSlotFromStore.day][index] = { ...unEditedSlotFromStore, cutOff: moment(unEditedSlotFromStore?.cutOff, TEXT_CONSTANT.SAVE_DATE_FORMAT) };
    });
    setConfigTabs(newTabs);
  };

  const handleHolidayEnabledChange = (configTabIndex: number) => {
    const newTabs = produce(configTabs, (draft: any) => {
      draft[configTabIndex].weeklyHolidayEnabled = !draft[configTabIndex].weeklyHolidayEnabled;
    });
    setConfigTabs(newTabs);
  };

  const handleWeeklyOffDaysChange = (configTabIndex: number, selected: string[]) => {
    const newTabs = produce(configTabs, (draft: any) => {
      const tab = draft[configTabIndex];
      if (!tab.holidayConfig) {
        tab.holidayConfig = { weeklyOffDays: [] };
      }
      tab.holidayConfig.weeklyOffDays = selected;
    });
    setConfigTabs(newTabs);
  };

  const onHandleDeleteTemplateSlot = async (tabIndex: number, generatorFormIndex: number, slotIndex: number, slot: any) => {
    if (!slot?.id) return;
    const {
      payload: { data, message: deleteError },
    }: any = await dispatch(
      deleteDefaultTemplateSlotBySlotId({
        serviceTypeName,
        slotData: { id: slot.id, zoneId: slot.zoneId, day: slot.day },
        objectKey: slot.isSupplier ? "supplier" : "carrefour",
      })
    );
    if (deleteError) {
      CustomAlert(ALERT_TYPES.ERROR, deleteError);
      return;
    }
    CustomAlert(ALERT_TYPES.SUCCESS, "Slot deleted successfully!");
    // Remove the slot from configTabs UI state
    let newTabs = produce(configTabs, (draft: any) => {
      const forms = draft[tabIndex].slotsGeneratorForms;
      const objectKey = slot.isSupplier ? "supplier" : "carrefour";
      if (forms?.[objectKey]?.[slot.day]) {
        forms[objectKey][slot.day] = forms[objectKey][slot.day].filter((_: any, i: number) => i !== slotIndex);
      }
    });
    setConfigTabs(newTabs);
  };

  const handleUpdateSlot = async ({ objectKey, tabIndex, generatorFormIndex, slotIndex, slot }: { objectKey: any, tabIndex: number, generatorFormIndex: number, slotIndex: number, slot: any }) => {
    const responseValue = validateUpdateSlotConfigObject(slot);
    if (!responseValue) {
      let data = await updateTemplateSlotHandler(tabIndex, generatorFormIndex, slotIndex, { ...slot, deliveryType: DELIVERY_TYPE.DAY_BASED }, serviceTypeName, serviceTypeId, dispatch, objectKey);
      if (
        data?.day &&
        configTabs?.[tabIndex]?.slotsGeneratorForms?.[objectKey as keyof slotsGeneratorFormsnterface]?.[data?.day]
      ) {
        let newTabs = produce(configTabs, (draft: any) => {
          draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][data.day][slotIndex] = { ...data, cutOff: moment(data?.cutOff, TEXT_CONSTANT.SAVE_DATE_FORMAT) };
        });
        setConfigTabs(newTabs);
      }
    }
  }

  const deleteConfigureSlotsOption = (tabIndex: any, objectKey: any, slotGenertorFormIndex: any) => {
    let newTabs = produce(configTabs, (draft) => {
      draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface].splice(slotGenertorFormIndex, 1);
    });
    setConfigTabs(newTabs);
  };

  const renderRangeGeneratorForm = (configTab: any, configTabIndex: number, generatorFormIndex: number, objectKey: any) => {
    let slotDetailsData = configTabs[configTabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][generatorFormIndex];
    let remainingDays = calculateAvailableDaysForSelection(generatorFormIndex, configTab, objectKey);

    return (
      <RangeGeneratorForm
        tabIndex={configTabIndex}
        formIndex={generatorFormIndex}
        remainingDays={remainingDays}
        days={slotDetailsData.days}
        priorityFee={slotDetailsData.priorityFee}
        minimumDays={slotDetailsData.minimumDays}
        maximumDays={slotDetailsData.maximumDays}
        bufferTimeHours={slotDetailsData.bufferTime}
        defaultSlotCapacity={slotDetailsData.defaultSlotCapacity}
        generatedRange={slotDetailsData.generatedRange}
        onDaysChange={handleDaysChange}
        onGenerateRangeSubmit={handleGenerateRangeSubmit}
        handleResetSlotsClick={(tabIndex: number, formIndex: number) => {
          let newTabs: any = produce(configTabs, (draft: any) => {
            draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][formIndex] = DEFAULT_RANGE_GENERATOR_FORM_VALUES;
          });

          setConfigTabs(newTabs);
        }}
        onIndividualRangeFieldsChange={(tabIndex: number, formIndex: number, rangeIndex: number, event: any) => {
          if (!isEmptyOrNull(event?.target)) {
            const { name, value } = event.target;
            if (value !== "" && name === "capacity" && !reg.test(value)) {
              return;
            }
            let newTabs: any = produce(configTabs, (draft: any) => {
              draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][formIndex].generatedRange[rangeIndex][name] = value;
              if (name === "capacity") {
                draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface][formIndex].defaultSlotCapacity = value;
              }
            });
            setConfigTabs(newTabs);
          }
        }}
        objectKey={objectKey}
        deleteConfigureSlotsOption={deleteConfigureSlotsOption}
        generatorFormIndex={generatorFormIndex}
        configTabIndex={configTabIndex}
        cutOff={slotDetailsData?.cutOff}
        onIndividualCutOffFieldsChange={onIndividualCutOffFieldsChange}
      />
    );
  };

  const renderRangeGeneratorFormForUpdate = (configTab: any, configTabIndex: number, objectKey: any) => {
    const slotDetailsData = configTab.slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface];
    const isEditMode = !!configTab.zoneId;
    let dayWiseSlots = isEditMode
      ? defaultService?.[configTabIndex]?.supplierTypes?.[objectKey]?.dayWiseSlots
      : null;
    return (
      <EditSlotRange
        tabIndex={configTabIndex}
        formIndex={0}
        generatedSlots={slotDetailsData}
        dayWiseSlots={dayWiseSlots}
        makeSlotEditable={(slot: any) => {
          if (!slot || !slot?.id) return;
          dispatch(makeDefaultTemplateSlotEditable({ serviceTypeName, slotData: slot, canBeUpdated: true, objectKey }));
        }}
        cancelEditingASlot={({ slotIndex, slot, generatorFormIndex }: { slotIndex: number, slot: any, generatorFormIndex: number }) => {
          cancelEditingASlot(configTabIndex, generatorFormIndex, slotIndex, slot, objectKey);
        }}
        onIndividualRangeFieldsChange={(tabIndex: number, generatedSlots: any) => {
          let newTabs: any = produce(configTabs, (draft: any) => {
            draft[tabIndex].slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface] = generatedSlots;
          });
          setConfigTabs(newTabs);
        }}
        addNewTemplateSlot={(index: any, slotInfo: any) => {
          let new_config_tabs = add_new_template_slot_util(configTabs, configTabIndex, index, slotInfo.day, posDetails);
          setConfigTabs(new_config_tabs);
        }}
        handleDeleteTemplateSlot={({ slotIndex, slot, generatorFormIndex }: { slotIndex: number, slot: any, generatorFormIndex: number }) => onHandleDeleteTemplateSlot(configTabIndex, generatorFormIndex, slotIndex, slot)}
        handleUpdateConfig={({ slotIndex, slot, generatorFormIndex }: { slotIndex: number, slot: any, generatorFormIndex: number }) => handleUpdateSlot({ objectKey, tabIndex: configTabIndex, generatorFormIndex, slotIndex, slot })}
      />
    );
  };

  const capitalizeFirstLetter = (nameString: any) => {
    return nameString.charAt(0).toUpperCase() + nameString.slice(1);
  };

  const renderConfigureSlots = (configTab: any, configTabIndex: number, objectKey: any) => {
    return (
      <div>
        {isArrayValid(configTab?.slotsGeneratorForms?.[objectKey as keyof slotsGeneratorFormsnterface])
          ? Object.keys(configTab.slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface]).map((slotGeneratorForm: any, generatorFormIndex: number) => (
            <div key={`renderConfigureSlotsAccordion-slotGeneratorForm-${generatorFormIndex}`} ref={rangeGeneratorFormRef}>
              {configTabs?.[configTabIndex]?.slotsGeneratorForms?.[objectKey as keyof slotsGeneratorFormsnterface] ? (
                renderRangeGeneratorForm(configTab, configTabIndex, generatorFormIndex, objectKey)
              ) : (
                null
              )}
            </div>
          ))
          : renderRangeGeneratorFormForUpdate(configTab, configTabIndex, objectKey)}
      </div>
    );
  }

  /* *****
  Rendering Delivery Type
  name could be "Carrefour Delivery"
  and objectKey could be "carrefour"
  */
  const renderConfigureSlotsAccordion = (configTab: any, configTabIndex: number, objectKey: any) => {
    const name = capitalizeFirstLetter(objectKey) + " Delivery";

    return (
      <Accordion defaultExpanded={false} className={classes.accordionStyle}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummaryStyle}>
          <Grid container alignItems="center">
            <Grid>
              <Typography className={classes.accordionTitle}>{capitalizeFirstLetter(name)}</Typography>
            </Grid>
          </Grid>
          {isArrayValid(configTab?.slotsGeneratorForms?.[objectKey as keyof slotsGeneratorFormsnterface]) && (
            <Grid item xs={6} className={classes.addOtherDays}>
              <LinkButton buttonLabel={"Add Other Days"} disabled={isAddOtherDaysButtonDisabled(configTabs, configTabIndex, objectKey)} onClick={(e: any) => handleAddOtherDaysClick(configTabIndex, e, objectKey)} />
            </Grid>
          )}
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetailsStyle}>
          {isArrayValid(configTab?.slotsGeneratorForms?.[objectKey as keyof slotsGeneratorFormsnterface])
            ? Object.keys(configTab.slotsGeneratorForms[objectKey as keyof slotsGeneratorFormsnterface]).map((slotGeneratorForm: any, generatorFormIndex: number) => (
              <div key={`renderConfigureSlotsAccordion-slotGeneratorForm-${generatorFormIndex}`} ref={rangeGeneratorFormRef}>
                {configTabs?.[configTabIndex]?.slotsGeneratorForms?.[objectKey as keyof slotsGeneratorFormsnterface] ? (
                  renderRangeGeneratorForm(configTab, configTabIndex, generatorFormIndex, objectKey)
                ) : (
                  null
                )}
              </div>
            ))
            : renderRangeGeneratorFormForUpdate(configTab, configTabIndex, objectKey)}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSlotsContent = (configTab: any, configTabIndex: number) => {
    if (!configTab?.slotsGeneratorForms) return null;

    return Object.keys(configTab.slotsGeneratorForms).reduce((acc: any[], deliveryKey: string) => {
      if (serviceTypeName.toUpperCase() !== SERVICE_TYPES.DEFAULT && deliveryKey !== TEXT_CONSTANT.CARREFOUR) return acc;
      const el = SERVICE_TYPES.DEFAULT === serviceTypeName.toUpperCase()
        ? renderConfigureSlotsAccordion(configTab, configTabIndex, deliveryKey)
        : renderConfigureSlots(configTab, configTabIndex, deliveryKey);
      acc.push(el);
      return acc;
    }, []);
  };

  const renderZoneConfigurationsCard = () => {
    return (
      <Card className={classes.cards}>
        <CardContent className={classes.zoneConfigContainer}>
          <TabContext value={selectedZoneTab.toString()}>
            <Grid container alignItems="center" className={classes.borderBelowTabs}>
              <Grid item xs={9}>
                <Tabs
                  variant="scrollable"
                  value={selectedZoneTab}
                  onChange={(e: any, tabIndex: number) => {
                    setSelectedZoneTab(tabIndex);
                  }}
                >
                  {configTabs.map((configTab, configTabIndex) => (
                    <Tab
                      id={`config-tab-${configTabIndex}`}
                      value={configTabIndex}
                      key={configTabIndex}
                      disableRipple
                      classes={{
                        labelIcon: classes.tabLabelIcon,
                      }}
                      label={configTab.zoneName}
                      icon={
                        (configTabs.length === 1 && configTabs[selectedZoneTab].zoneId) || configTabs.length > 1 ? (
                          <RemoveButton
                            className={classes.deleteConfigTabIcon}
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setSelectedZoneTab(configTabIndex);
                              setShowDeleteZCModal(true);
                            }}
                          />
                        ) : (
                          ""
                        )
                      }
                    ></Tab>
                  ))}
                </Tabs>
              </Grid>
              <Grid item xs={3} className={classes.textAlignRight}>
                <LinkButton buttonLabel="Add Zone Configuration" onClick={handleAddZoneTabClick} disabled={!configTabs[selectedZoneTab]?.zoneId} />
              </Grid>
            </Grid>

            {configTabs.map((configTab, configTabIndex) => (
              <TabPanel key={`renderZoneConfigurationsCard-configTab=${configTabIndex}`} className={classes.tabPanel} value={configTabIndex.toString()}>
                <AdditionalZoneConfiguration
                  weeklyHolidayEnabled={configTab.weeklyHolidayEnabled ?? false}
                  weeklyOffDays={configTab?.holidayConfig?.weeklyOffDays ?? []}
                  onHolidayEnabledChange={() => handleHolidayEnabledChange(configTabIndex)}
                  onWeeklyOffDaysChange={(selected: string[]) => handleWeeklyOffDaysChange(configTabIndex, selected)}
                />
                <Accordion className={classes.accordionStyle}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummaryStyle}>
                    <Grid container alignItems="center">
                      <Grid>
                        <Typography className={classes.accordionTitle}>Slots</Typography>
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetailsStyle}>
                    {renderSlotsContent(configTab, configTabIndex)}
                  </AccordionDetails>
                </Accordion>
                {isPOSMappingAreaBased && renderAssignedAreas(configTab, configTabIndex)}

                <Grid container className={classes.buttonsContainer}>
                  <Grid item xs={6}></Grid>
                  {configTab.zoneId ? (
                    <Grid item xs={6} className={classes.buttonsStyle}>
                      <PrimaryButton
                        buttonLabel={defaultService[selectedZoneTab] && defaultService[selectedZoneTab].isUpdating ? "Updating..." : `Update ${configTabs[selectedZoneTab].zoneName} Configurations`}
                        disabled={!!(defaultService[selectedZoneTab] && defaultService[selectedZoneTab].isUpdating)}
                        onClick={() => handleSaveOrUpdateZoneConfigurationsSubmit(selectedZoneTab, true)}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={6} className={classes.buttonsStyle}>
                      <PrimaryButton
                        buttonLabel={defaultService[selectedZoneTab] && defaultService[selectedZoneTab].isCreating ? "Creating..." : `Save ${configTabs[selectedZoneTab].zoneName} Configurations`}
                        disabled={!!(defaultService[selectedZoneTab] && (defaultService[selectedZoneTab].isCreating || defaultService[selectedZoneTab].isCreated))}
                        onClick={() => handleSaveOrUpdateZoneConfigurationsSubmit(selectedZoneTab)}
                      />
                    </Grid>
                  )}
                </Grid>
              </TabPanel>
            ))}
          </TabContext>
        </CardContent>
      </Card>
    );
  };

  async function handleSaveOrUpdateZoneConfigurationsSubmit(selectedZoneTab: any, isUpdate?: boolean) {
    const resolvedTab = resolveTabForSave(configTabs, selectedZoneTab);
    const resolvedConfigTabs = configTabs.map((tab, i) => (i === selectedZoneTab ? resolvedTab : tab));
    const responseValue = validateSaveDefaultConfigObject(resolvedConfigTabs, selectedZoneTab, isUpdate, serviceTypeName);
    if (!responseValue.isValid) {
      let tab = resolvedConfigTabs[selectedZoneTab];
      if (isUpdate) {
        let newTabs = produce(resolvedConfigTabs, (draft) => {
          draft[selectedZoneTab].isUpdating = true;
          draft[selectedZoneTab].isUpdated = false;
        });
        setConfigTabs(newTabs);
        let configurationsPayload = createPayloadForUpdatedefaultConfig(tab, posDetails.id, serviceTypeId, responseValue.allowedDeliveryTypes);

        configurationsPayload.holidayConfig = {
          weeklyOffDays: configTabs[selectedZoneTab]?.weeklyHolidayEnabled
            ? (configTabs[selectedZoneTab]?.holidayConfig?.weeklyOffDays ?? [])
            : [],
        };

        // Merge supplier into carrefour for non-default service types
        if (serviceTypeName.toUpperCase() !== 'DEFAULT' && configurationsPayload?.defaultServiceTemplateSlots) {
          const { carrefour = [], supplier = [] } = configurationsPayload.defaultServiceTemplateSlots;
          configurationsPayload.defaultServiceTemplateSlots = {
            carrefour: [...carrefour, ...supplier]
          };
        }
        updateZoneConfigurations(posNo || "", serviceTypeName, selectedZoneTab, configurationsPayload, dispatch, servicePropositionList);
      } else {
        let newTabs = produce(configTabs, (draft) => {
          draft[selectedZoneTab].isCreating = true;
        });
        setConfigTabs(newTabs);
        let configurationsPayload = createPayloadForSavedefaultConfig(tab, posDetails.id, serviceTypeId, posDetails, responseValue.allowedDeliveryTypes);

        configurationsPayload.holidayConfig = {
          weeklyOffDays: configTabs[selectedZoneTab]?.weeklyHolidayEnabled
            ? (configTabs[selectedZoneTab]?.holidayConfig?.weeklyOffDays ?? [])
            : [],
        };

        // Merge supplier into carrefour for non-default service types
        if (serviceTypeName.toUpperCase() !== 'DEFAULT' && configurationsPayload?.defaultServiceTemplateSlots) {
          const { carrefour = [], supplier = [] } = configurationsPayload.defaultServiceTemplateSlots;
          configurationsPayload.defaultServiceTemplateSlots = {
            carrefour: [...carrefour, ...supplier]
          };
        }

        await saveDefaultZoneConfigurations(posNo || "", serviceTypeName, selectedZoneTab, configurationsPayload, dispatch, servicePropositionList);
        // Re-fetch slots so the UI transitions to edit mode (renderRangeGeneratorFormForUpdate)
        // with fresh, complete data from the server.
        dispatch(fetchSlotsInformationByServiceType({ posNo, serviceType: serviceTypeId, serviceTypeName, servicePropositionList }));
      }
    }
  }

  const generateSlotForm = useCallback((supplierType: any) => {
    let slotsGeneratorForms: any = {};

    if (supplierType) {
      Object.keys(supplierType).forEach((type: any) => {
        slotsGeneratorForms = {
          ...slotsGeneratorForms,
          [type]: supplierType[type].dayWiseSlots,
        };
      });
    }

    if (slotsGeneratorForms && Object.keys(slotsGeneratorForms) && Object.keys.length !== currentAllowedDeliveryTypes.length) {
      const missingKey = getMissingDeliveryTypeFromObject(slotsGeneratorForms);
      if (missingKey && missingKey.length > 0) {
        missingKey.forEach((element: any) => {
          slotsGeneratorForms = {
            ...slotsGeneratorForms,
            [element]: [
              {
                ...DEFAULT_RANGE_GENERATOR_FORM_VALUES,
                days: ALL_DAYS_VALUES,
              },
            ],
          };
        });
      }
    }

    return slotsGeneratorForms;
  }, []);

  const savedConfigurationsHandler = useCallback(
    (slots: any) => {
      let newConfigTabs = slots.map((slot: any) => {
        const weeklyOffDays: string[] = slot?.holidayConfig?.weeklyOffDays ?? [];
        return {
          ...slot,
          zoneId: slot.zoneConfig.id,
          zoneName: slot.zoneConfig.zoneName,
          selectedAreas: slot.selectedAreas || [],
          foodTypes: ["NON_FOOD"],
          slotsGeneratorForms: generateSlotForm(slot.supplierTypes),
          weeklyHolidayEnabled: weeklyOffDays.length > 0,
        };
      });

      setConfigTabs(newConfigTabs);
    },
    [generateSlotForm]
  );


  useEffect(() => {
    if (
      defaultService?.length &&
      defaultService?.[selectedZoneTab] &&
      !defaultService[selectedZoneTab].createError &&
      !defaultService[selectedZoneTab].isCreating &&
      !defaultService[selectedZoneTab].isUpdating &&
      !defaultService[selectedZoneTab].updateError &&
      (
        configTabs[selectedZoneTab]?.zoneId !== defaultService[selectedZoneTab]?.zoneConfig?.id ||
        (defaultService[selectedZoneTab]?.isUpdated && !configTabs[selectedZoneTab]?.isUpdated)
      )
    ) {
      savedConfigurationsHandler(defaultService);
    }
  }, [defaultService, configTabs, selectedZoneTab, savedConfigurationsHandler]);

  useEffect(() => {
    // Refetch whenever the URL POS differs from the cached posDetails, so a stale
    // POS (from a prior editor visit) can never leak its posId/serviceTypeMappingId
    // into this POS's write payload (DOS-3285).
    if (posNo && String(posDetails?.posNo) !== String(posNo) && servicePropositionList?.length) {
      dispatch(fetchPOSDetailsByPOSNo(posNo));
    }
  }, [dispatch, posNo, serviceTypeId, serviceTypeName, posDetails.posNo, servicePropositionList.length]);

  useEffect(() => {
    const fetchSlots = () => {
      dispatch(fetchSlotsInformationByServiceType({ posNo, serviceType: serviceTypeId, serviceTypeName, servicePropositionList }));
    };
    if (posNo && serviceTypeId) {
      fetchSlots();
    }
  }, [dispatch, posNo, serviceTypeId, serviceTypeName]);

  const renderBasicServiceDtails = () => (
    <BasicServiceDetails
      serviceTypeName={serviceTypeName}
      servicePropositionList={servicePropositionList}
      displayName={displayName}
      posDetails={posDetails}
    />
  );

  const renderFooterButtons = () => {
    return (
      <Grid container direction="row" className={classes.footerButtons}>
        <Grid item xs={6}>
          <Grid container spacing={1} justifyContent="flex-start">
            <Grid item className={classes.footerButtonsStyle}>
              <Link to={`${APP_ROUTES.POS_LIST}`} className={classes.linkstyle}>
                <PrimaryButton buttonLabel={`${BUTTON_TEXT_CONSTANT.BACK_TO_POS_LIST}`} buttonVariant="outlined" disabled={false} />
              </Link>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={1} justifyContent="flex-end">
            <Grid item className={classes.footerButtonsStyle}>
              <PrimaryButton buttonLabel={`${BUTTON_TEXT_CONSTANT.BACK_TO_POS}`} buttonVariant="outlined" disabled={!configTabs.length} onClick={() => navigate(`${APP_ROUTES.COMPLETE_SETUP}/${posDetails.posNo}`)} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  /**
   * Empty redux state on unmount
   */
  useEffect(() => {
    return () => {
      dispatch(resetSlotsInformation());
    };
  }, [dispatch]);

  return (
    <div data-testid={generateElementIdentifier({ key: DATA_IDENTIFIER.DEFAULT_SLOTS, element: ELEMENT.DIV })}>
      {!fetchingDetails && !fetchingPosDetails && !fetchingSlots ? (
        <>
          <PageTitle title={`POS - ${posNo}`} />
          {renderBasicServiceDtails()}
          {renderZoneConfigurationsCard()}
          {renderFooterButtons()}
          {
            <ConfirmModal
              show={showDeleteZCModal}
              title="Delete Zone Config"
              description="All the data for the zone configuration would be permanently lost. Do you wish to proceed?"
              confirmButtonText="Approve"
              onClose={() => setShowDeleteZCModal(false)}
              onProceed={() => deleteConfigTab(selectedZoneTab)}
            />
          }
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default DefaultService;
